<template>
  <ContentWrap>
    <el-button
      v-hasPermi="['distribution:shareholder:level:create']"
      class="mb-12px"
      type="primary"
      @click="openForm()"
    >
      <Icon class="mr-5px" icon="ep:plus" />新增等级
    </el-button>
    <el-table v-loading="loading" :data="list" :stripe="true">
      <el-table-column align="center" label="编号" prop="id" width="70" />
      <el-table-column align="center" label="名称" prop="name" min-width="120" />
      <el-table-column align="center" label="分红比例%" prop="bonusRate" width="110" />
      <el-table-column align="center" label="升级阈值" prop="upgradeValue" width="120" />
      <el-table-column align="center" label="排序" prop="levelSort" width="80" />
      <el-table-column align="center" label="默认" width="80">
        <template #default="{ row }">
          <el-tag v-if="row.isDefault" type="success" size="small">是</el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="操作" width="220" fixed="right">
        <template #default="{ row }">
          <el-button
            v-hasPermi="['distribution:shareholder:level:update']"
            link
            type="primary"
            @click="openForm(row)"
          >
            编辑
          </el-button>
          <el-button
            v-if="!row.isDefault"
            v-hasPermi="['distribution:shareholder:level:update']"
            link
            type="success"
            @click="handleDefault(row)"
          >
            设默认
          </el-button>
          <el-button
            v-hasPermi="['distribution:shareholder:level:delete']"
            link
            type="danger"
            @click="handleDelete(row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
  </ContentWrap>

  <Dialog v-model="dialogVisible" :title="formData.id ? '编辑等级' : '新增等级'" width="480px">
    <el-form ref="formRef" v-loading="formLoading" :model="formData" :rules="rules" label-width="110px">
      <el-form-item label="名称" prop="name">
        <el-input v-model="formData.name" />
      </el-form-item>
      <el-form-item label="分红比例%" prop="bonusRate">
        <el-input-number v-model="formData.bonusRate" :min="0" :max="100" :precision="4" class="!w-full" />
      </el-form-item>
      <el-form-item label="升级阈值" prop="upgradeValue">
        <el-input-number v-model="formData.upgradeValue" :min="0" :precision="0" class="!w-full" />
        <el-text size="small" type="info">金额类为分；人数/单量为整数</el-text>
      </el-form-item>
      <el-form-item label="排序" prop="levelSort">
        <el-input-number v-model="formData.levelSort" :min="0" class="!w-full" />
      </el-form-item>
      <el-form-item label="默认等级" prop="isDefault">
        <el-switch v-model="formData.isDefault" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submit">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import * as LevelApi from '@/api/mall/trade/shareholder/level'

defineOptions({ name: 'TradeShareholderLevel' })

const message = useMessage()
const loading = ref(false)
const formLoading = ref(false)
const list = ref<LevelApi.ShareholderLevelVO[]>([])
const dialogVisible = ref(false)
const formRef = ref()
const formData = ref<LevelApi.ShareholderLevelVO>({
  name: '',
  bonusRate: 0,
  upgradeValue: 0,
  levelSort: 0,
  isDefault: false
})
const rules = reactive({
  name: [{ required: true, message: '名称不能为空', trigger: 'blur' }],
  bonusRate: [{ required: true, message: '比例不能为空', trigger: 'blur' }],
  levelSort: [{ required: true, message: '排序不能为空', trigger: 'blur' }],
  isDefault: [{ required: true, message: '请选择', trigger: 'change' }]
})

const getList = async () => {
  loading.value = true
  try {
    list.value = (await LevelApi.getShareholderLevelList()) || []
  } finally {
    loading.value = false
  }
}

const openForm = (row?: LevelApi.ShareholderLevelVO) => {
  formData.value = row
    ? { ...row }
    : { name: '', bonusRate: 0, upgradeValue: 0, levelSort: 0, isDefault: false }
  dialogVisible.value = true
}

const submit = async () => {
  await formRef.value?.validate()
  formLoading.value = true
  try {
    if (formData.value.id) {
      await LevelApi.updateShareholderLevel(formData.value)
    } else {
      await LevelApi.createShareholderLevel(formData.value)
    }
    message.success('保存成功')
    dialogVisible.value = false
    await getList()
  } finally {
    formLoading.value = false
  }
}

const handleDefault = async (row: LevelApi.ShareholderLevelVO) => {
  await message.confirm(`将「${row.name}」设为默认等级？`)
  await LevelApi.setDefaultShareholderLevel(row.id!)
  message.success('已设为默认')
  await getList()
}

const handleDelete = async (row: LevelApi.ShareholderLevelVO) => {
  try {
    await message.delConfirm()
    await LevelApi.deleteShareholderLevel(row.id!)
    message.success('已删除')
    await getList()
  } catch {}
}

onMounted(getList)
</script>
